import { AlertCircle } from "lucide-react-native";
import { useEffect, useMemo, useRef, useState } from "react";
import { ScrollView, Text, View } from "react-native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import { useLocale, useRegion, useTranslations } from "@src/packages/i18n";
import type { CostPaymentRecordParam, CostsStackParamList } from "../../types";
import RegionGatePlaceholder from "../../components/RegionGatePlaceholder";
import ScreenHeader from "../../components/ScreenHeader";
import GlassScreenLayout from "@louise/moon-ui/primitives/GlassScreenLayout";
import { SCREEN_PADDING_TOP } from "@louise/moon-ui/theme/tokens";
import { EMBER } from "../../../onboarding/theme";
import { useSmartBack } from "../../hooks/useSmartBack";
import {
  CostDocumentUploadModal,
  type ManualPaymentEntry,
  type UploadedCostDocument,
} from "./suggested/CostsOverviewConceptScreen";
import {
  useCreatePaymentRecord,
  usePaymentRecords,
  useUpdatePaymentRecord,
} from "@src/packages/costs/hooks/usePaymentRecords";
import {
  useSaveTaxDeductionProfile,
  useSaveTaxDeductionReview,
  useDeleteTaxDeductionReview,
  useTaxDeductionCalculation,
  useTaxDeductionProfile,
  useTaxDeductionReviews,
} from "@src/packages/costs/hooks/useTaxDeductions";
import {
  MEDICAL_DEDUCTION_TAX_YEAR,
  calculateMedicalExpenseDeduction,
} from "@src/packages/costs/medicalExpenseDeduction";
import {
  buildTaxReviewSummary,
  type TaxReviewEntry,
} from "@src/packages/costs/taxReview";
import PaymentRecordDetailModal from "./PaymentRecordDetailModal";
import {
  apiToUiPaymentRecord,
  parseDateText,
  todayIsoDate,
  uiToPaymentRecordUpdates,
} from "./paymentRecordMapping";
import { getDevTaxReviewFixture } from "./taxReviewFixtures";
import TaxReviewSections from "./TaxReviewSections";
import {
  attachDocumentToPaymentRecord,
  buildManualPaymentRecordPayload,
  buildUploadedPaymentRecordPayload,
  detachDocumentFromPaymentRecord,
  findPaymentRecordForUpload,
  itemizationChoice,
  itemizationValue,
  type ItemizationChoice,
} from "./taxReviewHelpers";
import { BackLink } from "@louise/moon-ui";

type Props = NativeStackScreenProps<CostsStackParamList, "TaxReview">;

export default function TaxReviewScreen({ route }: Props) {
  const t = useTranslations();
  const region = useRegion();
  const { locale } = useLocale();
  const taxYear = MEDICAL_DEDUCTION_TAX_YEAR;
  const paymentRecordsQuery = usePaymentRecords();
  const profileQuery = useTaxDeductionProfile(taxYear);
  const reviewsQuery = useTaxDeductionReviews(taxYear);
  const calculationQuery = useTaxDeductionCalculation(taxYear);
  const saveProfile = useSaveTaxDeductionProfile(taxYear);
  const saveTaxReview = useSaveTaxDeductionReview(taxYear);
  const deleteTaxReview = useDeleteTaxDeductionReview(taxYear);
  const createPaymentRecordMutation = useCreatePaymentRecord();
  const updatePaymentRecordMutation = useUpdatePaymentRecord();
  const devTaxReviewFixture = useMemo(
    () => getDevTaxReviewFixture(taxYear),
    [taxYear],
  );
  const [uploadVisible, setUploadVisible] = useState(false);
  const [selectedPaymentRecord, setSelectedPaymentRecord] =
    useState<CostPaymentRecordParam | null>(null);
  const [attachmentRecordId, setAttachmentRecordId] = useState<string | null>(
    null,
  );
  const [agiDraft, setAgiDraft] = useState<number | null>(null);
  const [itemizationDraft, setItemizationDraft] =
    useState<ItemizationChoice>("unknown");
  const [inlineInputError, setInlineInputError] = useState<string | null>(null);
  const inlineSaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const handleBack = useSmartBack({
    returnTab: route.params?.returnTab,
    fallbackTab: "Costs",
    fallbackScreen: "TaxDeductions",
  });
  const summary = useMemo(
    () =>
      buildTaxReviewSummary(
        devTaxReviewFixture?.records ?? paymentRecordsQuery.data ?? [],
        devTaxReviewFixture?.reviews ?? reviewsQuery.data ?? [],
        devTaxReviewFixture?.profile ?? profileQuery.data ?? null,
        taxYear,
      ),
    [
      devTaxReviewFixture,
      paymentRecordsQuery.data,
      profileQuery.data,
      reviewsQuery.data,
      taxYear,
    ],
  );
  const draftCalculation = useMemo(
    () =>
      calculateMedicalExpenseDeduction(
        summary.entries.map((entry) => entry.candidate),
        agiDraft,
        itemizationValue(itemizationDraft),
      ),
    [summary.entries, agiDraft, itemizationDraft],
  );

  useEffect(() => {
    setAgiDraft(summary.profile?.adjustedGrossIncome ?? null);
    setItemizationDraft(itemizationChoice(summary.profile?.itemizesDeductions));
    setInlineInputError(null);
  }, [summary.profile]);

  useEffect(
    () => () => {
      if (inlineSaveTimeoutRef.current) {
        clearTimeout(inlineSaveTimeoutRef.current);
      }
    },
    [],
  );

  const saveInlineTaxInputs = (
    adjustedGrossIncome: number | null,
    itemizesDeductions: boolean | null,
  ) => {
    if (adjustedGrossIncome === null) {
      setInlineInputError(t("costs.tax_review.invalid_agi"));
      return;
    }

    setInlineInputError(null);
    void saveProfile
      .mutateAsync({
        taxYear,
        adjustedGrossIncome,
        itemizesDeductions,
      })
      .catch((error: unknown) => {
        setInlineInputError(
          error instanceof Error
            ? error.message
            : t("costs.tax_review.save_tax_inputs_error"),
        );
      });
  };

  const scheduleInlineTaxInputs = (
    adjustedGrossIncome: number | null,
    itemizesDeductions: boolean | null,
  ) => {
    if (inlineSaveTimeoutRef.current) {
      clearTimeout(inlineSaveTimeoutRef.current);
    }

    if (adjustedGrossIncome === null) {
      setInlineInputError(t("costs.tax_review.invalid_agi"));
      return;
    }

    if (
      summary.profile?.adjustedGrossIncome === adjustedGrossIncome &&
      summary.profile.itemizesDeductions === itemizesDeductions
    ) {
      return;
    }

    inlineSaveTimeoutRef.current = setTimeout(() => {
      saveInlineTaxInputs(adjustedGrossIncome, itemizesDeductions);
    }, 350);
  };

  const openPaymentDetails = (entry: TaxReviewEntry) => {
    setSelectedPaymentRecord(apiToUiPaymentRecord(entry.record, locale));
  };

  const selectedTaxReviewEntry = selectedPaymentRecord
    ? summary.entries.find(
        (entry) => entry.record.id === selectedPaymentRecord.id,
      )
    : undefined;
  const selectedTaxReviewDecision =
    selectedTaxReviewEntry?.reviewStatus === "included"
      ? "include"
      : selectedTaxReviewEntry?.reviewStatus === "excluded"
        ? "exclude"
        : "unknown";

  const attachDocumentToRecord = async (document: UploadedCostDocument) => {
    if (!attachmentRecordId) throw new Error("No payment record selected");
    const entry = summary.entries.find(
      (candidate) => candidate.record.id === attachmentRecordId,
    );
    if (!entry) throw new Error("Payment record is no longer available");
    if (!document.documentId || !document.uploadId) {
      throw new Error(t("costs.tax_review.missing_document_linkage"));
    }

    const documents = attachDocumentToPaymentRecord(
      entry.record,
      document,
      t("costs.tax_review.missing_document_linkage"),
    );
    const updated = await updatePaymentRecordMutation.mutateAsync({
      paymentRecordId: entry.record.id,
      updates: { documents },
    });
    setSelectedPaymentRecord(apiToUiPaymentRecord(updated, locale));
    setAttachmentRecordId(null);
    await paymentRecordsQuery.refetch();
  };

  const createManualPaymentRecord = async (entry: ManualPaymentEntry) => {
    await createPaymentRecordMutation.mutateAsync({
      ...buildManualPaymentRecordPayload(entry, {
        missingDocumentLinkageMessage: t(
          "costs.tax_review.missing_document_linkage",
        ),
        parseDate: parseDateText,
        today: todayIsoDate(),
      }),
    });
    await paymentRecordsQuery.refetch();
  };

  const createUploadedPaymentRecord = async (
    document: UploadedCostDocument,
  ) => {
    const uploadId = document.uploadId;
    if (!document.documentId || !uploadId) {
      throw new Error(t("costs.tax_review.missing_document_linkage"));
    }
    const refreshedRecords = await paymentRecordsQuery.refetch();
    const existing = findPaymentRecordForUpload(
      refreshedRecords.data ?? [],
      uploadId,
    );
    if (existing) return;

    await createPaymentRecordMutation.mutateAsync({
      ...buildUploadedPaymentRecordPayload(document, {
        missingDocumentLinkageMessage: t(
          "costs.tax_review.missing_document_linkage",
        ),
        uploadedDocumentLabel: (kind) =>
          t("costs.tax_review.uploaded_document_label", {
            params: { kind },
          }),
        uploadedPhotoLabel: t("costs.tax_review.uploaded_photo"),
        uploadedFileLabel: t("costs.tax_review.uploaded_file"),
        today: todayIsoDate(),
      }),
    });
    await paymentRecordsQuery.refetch();
  };

  if (region !== "US") return <RegionGatePlaceholder />;

  const focusedId = route.params?.paymentRecordId;

  return (
    <GlassScreenLayout
      overlay={
        <>
          <CostDocumentUploadModal
            visible={uploadVisible}
            mode="payment"
            onClose={() => setUploadVisible(false)}
            onDocumentPicked={createUploadedPaymentRecord}
            onManualPaymentAdded={createManualPaymentRecord}
          />
          <CostDocumentUploadModal
            visible={attachmentRecordId !== null}
            mode="payment"
            documentOnly
            onClose={() => setAttachmentRecordId(null)}
            onDocumentPicked={attachDocumentToRecord}
            onManualPaymentAdded={async () => undefined}
          />
        </>
      }
    >
      <ScrollView
        contentContainerStyle={{
          paddingHorizontal: 16,
          paddingTop: SCREEN_PADDING_TOP,
          paddingBottom: 100,
          maxWidth: 520,
          width: "100%",
          alignSelf: "center",
          gap: 16,
        }}
        showsVerticalScrollIndicator={false}
      >
        <BackLink
          label={
            route.params?.returnTab
              ? t("you.shared.back")
              : t("costs.tax_deductions")
          }
          onPress={handleBack}
          style={{ marginBottom: 2 }}
        />

        <ScreenHeader
          title={t("costs.tax_review.tax_year_review")}
          subtitle={t("costs.tax_review.tax_year_review_subtitle", {
            params: { tax_year: String(taxYear) },
          })}
          showActions={false}
        />

        <TaxReviewSections
          summary={summary}
          calculation={draftCalculation}
          calculationSnapshot={calculationQuery.data}
          locale={locale}
          agiDraft={agiDraft}
          itemizationDraft={itemizationDraft}
          inlineInputError={inlineInputError}
          focusedId={focusedId}
          onAgiChange={(value) => {
            setAgiDraft(value);
            setInlineInputError(null);
            scheduleInlineTaxInputs(value, itemizationValue(itemizationDraft));
          }}
          onItemizationChange={(value) => {
            setItemizationDraft(value);
            scheduleInlineTaxInputs(agiDraft, itemizationValue(value));
          }}
          onAddExpense={() => setUploadVisible(true)}
          onOpenDetails={openPaymentDetails}
          infoSections={[]}
        />

        <View style={{ flexDirection: "row", gap: 10, padding: 8 }}>
          <AlertCircle
            size={16}
            color={EMBER.colors.textFaint}
            strokeWidth={1.5}
            style={{ marginTop: 1 }}
          />
          <Text
            className="font-dm-sans"
            style={{
              flex: 1,
              fontSize: 12,
              lineHeight: 18,
              color: EMBER.colors.textFaint,
            }}
          >
            {t("costs.tax_review.potential_result_disclaimer")}
          </Text>
        </View>
      </ScrollView>

      {selectedPaymentRecord ? (
        <PaymentRecordDetailModal
          record={selectedPaymentRecord}
          locale={locale}
          onClose={() => setSelectedPaymentRecord(null)}
          onAttachDocument={() =>
            setAttachmentRecordId(selectedPaymentRecord.id)
          }
          onDeleteDocument={async (documentId) => {
            if (!selectedPaymentRecord) return;
            const rawRecord = (
              devTaxReviewFixture?.records ??
              paymentRecordsQuery.data ??
              []
            ).find((candidate) => candidate.id === selectedPaymentRecord.id);
            if (!rawRecord) return;
            const documents = detachDocumentFromPaymentRecord(
              rawRecord,
              documentId,
            );
            const updated = await updatePaymentRecordMutation.mutateAsync({
              paymentRecordId: rawRecord.id,
              updates: { documents },
            });
            setSelectedPaymentRecord(apiToUiPaymentRecord(updated, locale));
            await paymentRecordsQuery.refetch();
          }}
          onSaveRecord={async (record) => {
            const updated = await updatePaymentRecordMutation.mutateAsync({
              paymentRecordId: record.id,
              updates: uiToPaymentRecordUpdates(record),
            });
            setSelectedPaymentRecord(apiToUiPaymentRecord(updated, locale));
            await paymentRecordsQuery.refetch();
          }}
          taxEligibility={selectedPaymentRecord.taxEligibility}
          onTaxEligibilityChange={async (taxEligibility) => {
            if (!selectedPaymentRecord) return;
            const updated = await updatePaymentRecordMutation.mutateAsync({
              paymentRecordId: selectedPaymentRecord.id,
              updates: { taxEligibility },
            });
            setSelectedPaymentRecord(apiToUiPaymentRecord(updated, locale));
            await paymentRecordsQuery.refetch();
          }}
          taxReviewDecision={selectedTaxReviewDecision}
          onTaxReviewDecision={async (decision) => {
            if (!selectedPaymentRecord) return;
            await saveTaxReview.mutateAsync({
              paymentRecordId: selectedPaymentRecord.id,
              decision,
            });
          }}
          onResetTaxReviewDecision={async () => {
            if (!selectedPaymentRecord) return;
            await deleteTaxReview.mutateAsync(selectedPaymentRecord.id);
          }}
        />
      ) : null}
    </GlassScreenLayout>
  );
}
